import React from "react";
import { Button } from "../../ui/button";
import Image from "../../Tags/Image";
import { useTheme } from "../../ThemeProvider";
import { Play } from "lucide-react";
import PageHeader from "../../PageHeader";

export default function DialerSplash({ heading = "Dialer" }) {
  const { theme } = useTheme();

  const features = [
    {
      id: 1,
      icon: "/images/icons/untitledui/star-icon.svg",
      iconDark: "/images/icons/untitledui/star-icon-dark.svg",
      title: "Deep Call Analysis",
      description:
        "Get summaries, key topics, sentiment, talk-time breakdowns, action items, and searchable transcripts moments after each call.",
    },
    {
      id: 2,
      icon: "/images/icons/untitledui/graph-icon.svg",
      iconDark: "/images/icons/untitledui/graph-icon-dark.svg",
      title: "Team-Level Visibility",
      description:
        "Understand calling patterns across reps with AI-driven insights into activity, engagement, and conversation trends.",
    },
    {
      id: 3,
      icon: "/images/icons/untitledui/Featured-icon-3.svg",
      iconDark: "/images/icons/untitledui/Featured-icon-3-dark.svg",
      title: "Global Calling",
      description:
        "Call prospects and customers across regions using assigned local or international numbers, all managed from PrimeRole.",
    },
  ];

  const handleGetStarted = () => {
    window.open("https://meetings-na2.hubspot.com/primerole?uuid=27e8b717-9c67-48fc-ae8f-4b90fca1f7f3", "_blank");
  };

  const handleSeeInAction = () => {
    window.open(`${process.env.REACT_APP_BASE_URL}/docs/sales-dialer/using-primerole-dialer`, "_blank");
  };

  const getDescription = () => {
    if (heading.toLowerCase().includes("call") && heading.toLowerCase().includes("recording")) {
      return "View call logs, transcripts and analyze calls with AI";
    }
    return "Make and manage calls directly from your workspace.";
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-dark-50 pt-6">
      <PageHeader
        heading={heading}
        description={getDescription()}
      />

      {/* Header Section */}
      <div className="py-6">
        <div className="mb-3">
          <span className="text-[16px] font-semibold text-[#6941C6] dark:text-[#A78BFA]">
            Enable dialer to unlock
          </span>
        </div>
        <h1 className="text-4xl font-semibold text-[#181D27] dark:text-white mb-5">
          Dialing That Captures More
        </h1>
        <p className="text-xl text-[#535862] dark:text-gray-400 font-normal">
          AI-powered calling that records, transcribes, and analyzes conversations, helping teams stay
          <br />
          focused during calls and informed after they end.
        </p>
      </div>

      {/* Main Content */}
      <div className="pb-12 -mr-[32px]">
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left Side */}
            <div className="space-y-6">
             
              <div>
                <p className="text-base text-[#535862] dark:text-gray-400 font-normal">
                  Enable the dialer to bring calling into your workflow
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleGetStarted}
                  className="bg-[#7F56D9] hover:bg-[#6941C6] text-white px-4 py-3 rounded-lg shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),inset_0px_-2px_0px_0px_rgba(10,13,18,0.05),inset_0px_0px_0px_1px_rgba(10,13,18,0.18)] outline outline-2 outline-offset-[-2px] outline-white/10 inline-flex justify-center items-center gap-1.5 overflow-hidden font-semibold text-base"
                >
                  Get started
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSeeInAction}
                  className="border-[#D5D7DA] dark:border-gray-600 text-[#414651] dark:text-white hover:bg-gray-50 dark:hover:bg-gray-400 px-6 py-2.5 rounded-lg shadow-md flex items-center gap-2 font-semibold text-sm"
                >
                  <Play size={16} />
                  See it in action
                </Button>
              </div>

              {/* Features */}
              <div className="space-y-4 pt-6">
                {features.map((feature) => (
                  <div key={feature.id}>
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Image
                          src={theme === "dark" ? feature.iconDark : feature.icon}
                          alt={feature.title}
                          width={48}
                          height={48}
                          className="text-[#7F56D9] no-invert"
                        />
                      </div>
                      <div className="flex-1 mt-3">
                        <h3 className="text-lg font-semibold text-[#181D27] dark:text-white mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-base text-[#535862] dark:text-gray-400 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Sid Image */}
            <div className="lg:sticky lg:top-8">
              <div className="relative">
                <div className="overflow-hidden">
                  {theme === "dark" ? (
                    <Image
                      src="/images/icons/untitledui/dialer-right-dark.svg"
                      alt="Dialer Preview"
                      className="w-full h-auto no-invert"
                    />
                  ) : (
                    <Image
                      src="/images/icons/untitledui/Dialer-right.svg"
                      alt="Dialer Preview"
                      className="w-full h-auto"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
